// WHAT IS THIS FILE? — the classifier. ONE combined FE export in, a full classification out.
//
// This is the module that lets the drop point stop asking "What is this file?". The CIO's file
// contains portfolios, holdings, benchmarks, benchmark constituents, ARC, AFI and IA sectors all
// at once; the dropdown asked a human to pick ONE of those, which was never a question the file
// could answer. So the rows get classified here instead, and the answer comes back with its own
// audit attached: what was found, what is missing by name, and whether the numbers reconcile.
//
// ⚠️ NOTHING IN HERE THROWS ON BAD DATA. Refusals and warnings are RETURN VALUES.
//
// The eight parser traps, and where each is handled:
//   1  " in GB" / " TR in GB" suffixes          normalise.ts  stripFeSuffix
//   2  embedded date stamps that move           this file     opaqueSuffix — matched, never keyed
//   3  "CBAM P4 -V1 V2 Dynamic" stray hyphen    this file     CBAM_BOOK_RX
//   4  three identical Vanguard rows            this file     the two-case de-duplication rule —
//                                                             identical name AND value collapse
//                                                             (reported); same name, different
//                                                             value REFUSES
//   5  "IA Not yet assigned" with no value      normalise.ts  parseFeValue -> null, never 0
//   6  share classes stripped between pulls     fundRoster.ts + peer rung patterns allow a
//                                                             trailing class remainder
//   7  the Fidelity Japan orphan                fundRoster.ts exact-or-nothing matching
//   8  header on line 3, commas inside line 2   parseFePerformanceTable.ts

import { matchHolding, buildFundRosterIndex, type FundRosterIndex } from "./fundRoster";
import { normaliseKey, parseFeValue, stripFeSuffix } from "./normalise";
import {
  AFI_LEVEL,
  ARC_BAND_LEVEL,
  LEVEL_BY_ROMAN,
  PEER_CELL_COUNT,
  PEER_RUNGS,
  RISK_LEVELS,
  ROMAN_BY_LEVEL,
  allPeerCells,
  peerCellKey,
} from "./peerLadder";
import { parseFePerformanceTable } from "./parseFePerformanceTable";
import type {
  AfiBlendCheck,
  ClassifiedRow,
  ClassificationCounts,
  ClassifyOptions,
  CompositeLevelCheck,
  DedupeReport,
  DuplicateCollapse,
  DuplicateConflict,
  FeCombinedClassification,
  IdentityCollapse,
  IdentityConflict,
  FeRowClass,
  PeerHouseId,
  Refusal,
  RiskLevel,
  Warning,
} from "./types";

// ----------------------------------------------------------------------------
// Patterns
// ----------------------------------------------------------------------------

/**
 * `CBAM {I|II|III|IV|V} Benchmark <opaque> `.
 *
 * ⚠️ TRAP 2. The `<opaque>` tail is a date stamp that FE RESTAMPS BETWEEN PULLS while the values
 * do not move. One real export carried three different stamps in a single file for one identical
 * period: I/II/III at 01/08/2026, IV at 01/05/2026, V at 01/07/2026. It is matched as `.*`,
 * recorded verbatim for the audit trail, and never compared to anything.
 */
const CBAM_BENCHMARK_RX = /^CBAM\s+(I{1,3}|IV|V)\s+Benchmark\b\s*(.*)$/i;

/**
 * `CBAM P{n} …` / `CBAM R{n} …`.
 *
 * ⚠️ "BM" in `CBAM R{n} BM Dynamic` is part of the R BOOK's FE series name. It is NOT a benchmark.
 * The CIO ruled exactly this on 2026-08-10; `sync-risk-daily-values` reads the same tokens off the
 * `CBAM Perf` sheet headers as `CBAM P{n} - V1 V2` / `CBAM R{n} - BM`. It lands in opaqueSuffix.
 *
 * ⚠️ TRAP 3. `CBAM P4 -V1 V2 Dynamic` carries a stray hyphen the other four lack. It falls inside
 * the opaque tail, so the level token still matches. The optional `-?` before the digit covers the
 * other placement (`CBAM P-4`) in case a future pull moves it.
 */
const CBAM_BOOK_RX = /^CBAM\s+(P|R|D)\s*-?\s*([1-5])(?!\d)\s*(.*)$/i;
const CBAM_MM_RX = /^CBAM\s+MM\b\s*(.*)$/i;
const CBAM_DIST_RX = /^CBAM\s+Dist\s*-?\s*([1-5])?\b\s*(.*)$/i;

/** `AFI Cautious` / `AFI Balanced` / `AFI Aggressive`. Level 1 has no AFI comparator at all. */
const AFI_RX = /^AFI\s+(Cautious|Balanced|Aggressive)(?![\w])\s*(.*)$/i;
/** FE occasionally supplies the level-4 blend pre-computed; when it does, F8 becomes evaluable. */
const AFI_BLEND_RX =
  /^AFI\s+(?:Balanced\s*[:/]\s*Aggressive|Aggressive\s*[:/]\s*Balanced)\s*50\s*[:/]\s*50\b/i;
/** `ARC MPS 0 20% Index` — FE separates the band with a space, not a hyphen. Accept either. */
const ARC_RX = /^ARC\s+MPS\s+(\d{1,3})\s*[-\s]\s*(\d{1,3})\s*%\s+Index\b/i;
/** `IA <sector>` — 57 category returns, equal-weight and NET OF OCF (CIO). */
const IA_RX = /^IA\s+(.+)$/i;

/** ⚠️ FE's currency baseline. Not a fund. Never bind it to the CASH pseudo-fund. */
const CURRENCY_BASELINE_KEYS = new Set(["pounds sterling", "us dollar", "euro"]);

/** The 11 books a complete export must carry. `DIST` is recognised but not required. */
export const REQUIRED_BOOK_CODES: readonly string[] = [
  "P1", "P2", "P3", "P4", "P5",
  "R1", "R2", "R3", "R4", "R5",
  "MM",
];

const DEFAULT_COMPOSITE_TOLERANCE = 0.02;
const DEFAULT_IA_SECTOR_COUNT = 57;

// ----------------------------------------------------------------------------
// Per-row classification
// ----------------------------------------------------------------------------

interface RowClassification {
  klass: FeRowClass;
  apply: (row: ClassifiedRow) => void;
}

/**
 * Every class this name could be. Normally length 1. Length > 1 means two patterns overlap, which
 * is a bug in the patterns, not in the data — it is surfaced as `row_matched_multiple_classes`
 * rather than silently resolved, because a name that is two things is a name we do not understand.
 */
function candidateClasses(name: string, index: FundRosterIndex | null): RowClassification[] {
  const out: RowClassification[] = [];
  const key = normaliseKey(name);

  if (CURRENCY_BASELINE_KEYS.has(key)) {
    out.push({
      klass: "unclassified",
      apply: (r) => {
        r.unclassifiedReason = "fe_currency_baseline";
      },
    });
    return out; // exact, closed, and must never be contested by the holding fallback
  }

  const bm = CBAM_BENCHMARK_RX.exec(name);
  if (bm) {
    const level = LEVEL_BY_ROMAN[bm[1].toUpperCase()];
    if (level) {
      out.push({
        klass: "cbam_benchmark",
        apply: (r) => {
          r.benchmark = { level, roman: bm[1].toUpperCase(), opaqueSuffix: bm[2].trim() };
        },
      });
    }
  }

  const mm = CBAM_MM_RX.exec(name);
  if (mm) {
    out.push({
      klass: "cbam_book",
      apply: (r) => {
        r.book = { series: "MM", level: null, code: "MM", opaqueSuffix: mm[1].trim() };
      },
    });
  }

  const dist = !mm ? CBAM_DIST_RX.exec(name) : null;
  if (dist) {
    const lvl = dist[1] ? (Number(dist[1]) as RiskLevel) : null;
    out.push({
      klass: "cbam_book",
      apply: (r) => {
        r.book = {
          series: "DIST",
          level: lvl,
          code: lvl ? `DIST${lvl}` : "DIST",
          opaqueSuffix: (dist[2] ?? "").trim(),
        };
      },
    });
  }

  const book = !mm && !dist ? CBAM_BOOK_RX.exec(name) : null;
  if (book) {
    const seriesChar = book[1].toUpperCase();
    const series = seriesChar === "D" ? "DIST" : (seriesChar as "P" | "R");
    const level = Number(book[2]) as RiskLevel;
    out.push({
      klass: "cbam_book",
      apply: (r) => {
        r.book = {
          series,
          level,
          code: `${series === "DIST" ? "DIST" : series}${level}`,
          opaqueSuffix: book[3].trim(),
        };
      },
    });
  }

  for (const rung of PEER_RUNGS) {
    if (rung.pattern.test(name)) {
      out.push({
        klass: "peer_constituent",
        apply: (r) => {
          r.peer = {
            house: rung.house,
            houseLabel: rung.houseLabel,
            level: rung.level,
            product: rung.product,
          };
        },
      });
      break; // PEER_RUNGS is sorted most-specific-first; one house owns one rung
    }
  }

  if (AFI_BLEND_RX.test(name)) {
    out.push({
      klass: "external_comparator",
      apply: (r) => {
        r.comparator = {
          family: "afi",
          label: "Balanced:Aggressive 50:50",
          level: 4,
          entityRef: "BM.4.afi",
          suppliedBlend: true,
        };
      },
    });
  } else {
    const afi = AFI_RX.exec(name);
    if (afi) {
      const label = afi[1][0].toUpperCase() + afi[1].slice(1).toLowerCase();
      const level = AFI_LEVEL[label.toLowerCase()] ?? null;
      out.push({
        klass: "external_comparator",
        apply: (r) => {
          r.comparator = {
            family: "afi",
            label,
            level,
            entityRef: level ? `BM.${level}.afi` : null,
          };
        },
      });
    }
  }

  const arc = ARC_RX.exec(name);
  if (arc) {
    const band = `${Number(arc[1])}-${Number(arc[2])}`;
    const level = ARC_BAND_LEVEL[band] ?? null;
    out.push({
      klass: "external_comparator",
      apply: (r) => {
        r.comparator = {
          family: "arc",
          label: band,
          level,
          entityRef: level ? `BM.${level}.arc` : null,
        };
      },
    });
  }

  const ia = IA_RX.exec(name);
  if (ia) {
    out.push({
      klass: "ia_sector",
      apply: (r) => {
        r.iaSector = ia[1].trim();
      },
    });
  }

  if (out.length === 0) {
    if (!name.trim()) {
      out.push({
        klass: "unclassified",
        apply: (r) => {
          r.unclassifiedReason = "no_class_matched";
        },
      });
    } else {
      // Everything that is not a CBAM series, a peer, a comparator or an IA sector is an
      // individual fund holding. The bind may fail; the CLASS does not.
      out.push({
        klass: "holding",
        apply: (r) => {
          r.holding = matchHolding(name, index);
        },
      });
    }
  }

  return out;
}

// ----------------------------------------------------------------------------
// The entry point
// ----------------------------------------------------------------------------

export function classifyCombinedExport(
  csvText: string,
  options: ClassifyOptions = {},
): FeCombinedClassification {
  const tolerance = options.compositeTolerance ?? DEFAULT_COMPOSITE_TOLERANCE;
  const expectedIa = options.expectedIaSectors ?? DEFAULT_IA_SECTOR_COUNT;
  const index = options.fundRoster ? buildFundRosterIndex(options.fundRoster) : null;

  const refusals: Refusal[] = [];
  const warnings: Warning[] = [];

  const table = parseFePerformanceTable(csvText);

  const counts: ClassificationCounts = {
    cbam_book: 0,
    cbam_benchmark: 0,
    peer_constituent: 0,
    external_comparator: 0,
    ia_sector: 0,
    holding: 0,
    unclassified: 0,
    total: 0,
  };

  if (!table.shapeOk) {
    refusals.push({
      code: "fe_preamble_shape",
      message:
        `This is not an FE Performance Table: ${table.shapeProblem}. ` +
        `Expected line 1 to be the report title, line 2 the pricing/period/currency basis, ` +
        `and line 3 the header "Name","<metric>".`,
      detail: { problem: table.shapeProblem, recordsFound: table.allRecords.length },
    });
    return {
      ok: false,
      provenance: table.provenance,
      rows: [],
      counts,
      dedupe: {
        rowsRead: 0,
        rowsRemoved: 0,
        collapsed: [],
        conflicts: [],
        identityCollapsed: [],
        identityConflicts: [],
      },
      composite: emptyComposite(tolerance),
      afiBlend: { level: 4, balanced: null, aggressive: null, computed: null, supplied: null, diff: null },
      refusals,
      warnings,
    };
  }

  if (!/performance\s+table/i.test(table.provenance.title)) {
    warnings.push({
      code: "title_unexpected",
      message: `Line 1 reads "${table.provenance.title}" rather than "Performance Table". The file parsed anyway; check it is the export you meant.`,
      detail: { title: table.provenance.title },
    });
  }

  const missingProvenance = (
    [
      ["pricingSpread", table.provenance.pricingSpread],
      ["currency", table.provenance.currency],
      ["windowStart", table.provenance.windowStart],
      ["windowEnd", table.provenance.windowEnd],
    ] as const
  )
    .filter(([, v]) => v === null)
    .map(([k]) => k);
  if (missingProvenance.length > 0) {
    warnings.push({
      code: "provenance_incomplete",
      message: `The basis line does not state ${missingProvenance.join(", ")}. The raw line is retained, but these are the provenance of every number in the file.`,
      detail: { missing: missingProvenance, basisRaw: table.provenance.basisRaw },
    });
  }

  // ---------------------------------------------------------------- classify
  // EVERY data line, before de-duplication. `rows` (the landable set) is derived from this below;
  // `allRows` is kept only so the collapse can be reported against what the file actually said.
  const allRows: ClassifiedRow[] = [];

  table.records.forEach((rec, i) => {
    const rawName = (rec.fields[0] ?? "").trim();
    // ⚠️ TRAP 5. `fields[1]` is genuinely absent on some rows — undefined, not "". Both mean
    // "no value", and neither means zero.
    const rawValue = rec.fields.length > 1 ? rec.fields[1] : null;
    const { name, totalReturn } = stripFeSuffix(rawName);
    const parsed = parseFeValue(rawValue);

    const row: ClassifiedRow = {
      line: rec.line,
      index: i,
      rawName,
      normalisedName: name,
      totalReturn,
      rawValue,
      value: parsed.value,
      klass: "unclassified",
    };

    if (parsed.unparseable) {
      warnings.push({
        code: "unparseable_value",
        message: `Line ${rec.line}: "${rawName}" has a value field that is not a number (${JSON.stringify(rawValue)}). Recorded as no value, not as zero.`,
        detail: { line: rec.line, name: rawName, rawValue },
      });
    }

    const candidates = candidateClasses(name, index);
    if (candidates.length > 1) {
      warnings.push({
        code: "row_matched_multiple_classes",
        message: `Line ${rec.line}: "${rawName}" matched more than one class (${candidates
          .map((c) => c.klass)
          .join(", ")}). The first was used; the patterns need narrowing.`,
        detail: { line: rec.line, name: rawName, classes: candidates.map((c) => c.klass) },
      });
    }
    const chosen = candidates[0];
    row.klass = chosen.klass;
    chosen.apply(row);

    allRows.push(row);
  });

  // ---------------------------------------------------------------- duplicates
  // ⚠️ TRAP 4, and the CIO's instruction of 2026-08-11 ("remove duplicates from the CSV").
  //
  // TWO CASES, TREATED DIFFERENTLY ON PURPOSE:
  //
  //   * IDENTICAL name AND value — three byte-identical "Vanguard US Government Bond Index Hedged
  //     Dis GBP TR in GB" rows at -1.09. `ba_cbam_funds` holds exactly ONE such fund
  //     (IE00BDD0SS10), so these are an FE export artefact, not three share classes. The first is
  //     KEPT, the rest REMOVED, and the collapse is reported by name, value and line number.
  //     Nothing is lost — every copy said the same thing.
  //
  //   * SAME name, DIFFERENT values — a CONFLICT. The file says two incompatible things about one
  //     series. Collapsing would mean picking one silently, and the survivor would then read as a
  //     figure nobody disagreed about. So neither is collapsed, both stay in place so every
  //     downstream check still sees the clash, and the file is REFUSED with both values named.
  //
  // Name equality is on the RAW FE name (see types.ts): " in GB" and " TR in GB" are a price
  // return and a total return — two quantities that merely share a stripped name. Value equality
  // is on the PARSED number, so "-1.09" and "-1.090" are one value, while a number against a
  // NO-VALUE is a conflict rather than a collapse.
  const valueKey = (r: ClassifiedRow): string => (r.value === null ? "none" : `n:${r.value}`);

  const byName = new Map<string, ClassifiedRow[]>();
  for (const r of allRows) {
    const list = byName.get(r.rawName);
    if (list) list.push(r);
    else byName.set(r.rawName, [r]);
  }

  const collapsed: DuplicateCollapse[] = [];
  const conflicts: DuplicateConflict[] = [];
  const removed = new Set<ClassifiedRow>();

  for (const [name, group] of byName) {
    if (group.length < 2) continue;

    const variants = new Map<string, ClassifiedRow[]>();
    for (const r of group) {
      const k = valueKey(r);
      const list = variants.get(k);
      if (list) list.push(r);
      else variants.set(k, [r]);
    }

    if (variants.size > 1) {
      conflicts.push({
        name,
        klass: group[0].klass,
        variants: Array.from(variants.values()).map((v) => ({
          value: v[0].value,
          rawValue: v[0].rawValue,
          lines: v.map((r) => r.line),
        })),
      });
      continue; // nothing in a conflicted group is collapsed, and nothing is chosen
    }

    const [keep, ...rest] = group;
    for (const r of rest) removed.add(r);
    collapsed.push({
      name,
      value: keep.value,
      rawValues: Array.from(new Set(group.map((r) => r.rawValue))),
      klass: keep.klass,
      count: group.length,
      lines: group.map((r) => r.line),
      keptLine: keep.line,
      removedLines: rest.map((r) => r.line),
    });
  }

  // Survivors of the NAME pass. Every survivor keeps its original `line` and `index` — those are
  // provenance, not array positions, and a finding must still point at the physical line.
  const nameSurvivors = removed.size > 0 ? allRows.filter((r) => !removed.has(r)) : allRows;

  // ------------------------------------------------------- duplicates, pass 2: RESOLVED IDENTITY
  // The name key above cannot see a fund the export spelled two ways. This pass keys on what the
  // classifier RESOLVED — `ba_cbam_funds.id` — and applies the SAME two-case rule. See the long
  // note on IdentityCollapse in types.ts for the measured evidence and for why `totalReturn` is
  // part of the key (a price return and a total return of one fund are two quantities, and SHOULD
  // differ; keying on the id alone would refuse a good file).
  //
  // Runs on the name-pass survivors, so a row already removed as an exact copy is not counted
  // twice, and `rowsRemoved` stays the true total across both passes.
  const identityCollapsed: IdentityCollapse[] = [];
  const identityConflicts: IdentityConflict[] = [];
  const identityRemoved = new Set<ClassifiedRow>();

  const byIdentity = new Map<string, ClassifiedRow[]>();
  for (const r of nameSurvivors) {
    // Only holdings carry a resolved fund identity, and only a NON-NULL one is an identity at all.
    const fundId = r.klass === "holding" ? r.holding?.fundId : null;
    if (!fundId) continue;
    const key = `${fundId}|tr:${r.totalReturn ? "1" : "0"}`;
    const list = byIdentity.get(key);
    if (list) list.push(r);
    else byIdentity.set(key, [r]);
  }

  for (const group of byIdentity.values()) {
    if (group.length < 2) continue;

    const variants = new Map<string, ClassifiedRow[]>();
    for (const r of group) {
      const k = valueKey(r);
      const list = variants.get(k);
      if (list) list.push(r);
      else variants.set(k, [r]);
    }

    const fundId = group[0].holding!.fundId!;
    const isin = group[0].holding!.isin;
    const totalReturn = group[0].totalReturn;

    if (variants.size > 1) {
      identityConflicts.push({
        fundId,
        isin,
        totalReturn,
        variants: Array.from(variants.values()).map((v) => ({
          value: v[0].value,
          rawValue: v[0].rawValue,
          names: Array.from(new Set(v.map((r) => r.rawName))),
          lines: v.map((r) => r.line),
        })),
      });
      continue; // nothing in a conflicted group is collapsed, and nothing is chosen
    }

    const [keep, ...rest] = group;
    for (const r of rest) identityRemoved.add(r);
    identityCollapsed.push({
      fundId,
      isin,
      totalReturn,
      value: keep.value,
      names: Array.from(new Set(group.map((r) => r.rawName))),
      count: group.length,
      lines: group.map((r) => r.line),
      keptLine: keep.line,
      keptName: keep.rawName,
      removedLines: rest.map((r) => r.line),
    });
  }

  const rows =
    identityRemoved.size > 0
      ? nameSurvivors.filter((r) => !identityRemoved.has(r))
      : nameSurvivors;

  const dedupe: DedupeReport = {
    rowsRead: allRows.length,
    rowsRemoved: removed.size + identityRemoved.size,
    collapsed,
    conflicts,
    identityCollapsed,
    identityConflicts,
  };

  for (const r of rows) {
    counts[r.klass] += 1;
    counts.total += 1;
  }

  if (collapsed.length > 0) {
    warnings.push({
      code: "duplicate_rows",
      message:
        `${collapsed.length} name${collapsed.length === 1 ? "" : "s"} appeared more than once carrying an ` +
        `IDENTICAL value, so the extra copies were removed: ${removed.size} row${
          removed.size === 1 ? "" : "s"
        } dropped, ${rows.length} of the ${allRows.length} read kept. ` +
        collapsed
          .map(
            (g) =>
              `"${g.name}" x${g.count} at ${g.value === null ? "no value" : g.value} ` +
              `(lines ${g.lines.join(", ")} — kept ${g.keptLine}, removed ${g.removedLines.join(", ")})`,
          )
          .join("; ") +
        `. Rows sharing a name but carrying DIFFERENT values are never collapsed — that is a ` +
        `conflict, and it refuses the file rather than picking one.`,
      detail: {
        groups: collapsed.map((g) => ({
          name: g.name,
          value: g.value,
          rawValues: g.rawValues,
          count: g.count,
          lines: g.lines,
          keptLine: g.keptLine,
          removedLines: g.removedLines,
          klass: g.klass,
        })),
        rowsRead: allRows.length,
        rowsRemoved: removed.size,
        rowsKept: rows.length,
      },
    });
  }

  if (identityCollapsed.length > 0) {
    const removedCount = identityCollapsed.reduce((n, g) => n + g.removedLines.length, 0);
    warnings.push({
      code: "duplicate_holdings_same_fund",
      message:
        `${identityCollapsed.length} fund${identityCollapsed.length === 1 ? " was" : "s were"} written under ` +
        `MORE THAN ONE NAME and resolved to a single ba_cbam_funds row carrying the same value, so the ` +
        `extra spellings were removed: ${removedCount} row${removedCount === 1 ? "" : "s"} dropped. ` +
        `The names differ, so the name-level collapse above could not see these — they are one holding, ` +
        `not two, and would otherwise have landed twice: ` +
        identityCollapsed
          .map(
            (g) =>
              `${g.names.map((n) => `"${n}"`).join(" = ")} -> fund ${g.fundId}` +
              `${g.isin ? ` (${g.isin})` : ""} at ${g.value === null ? "no value" : g.value}` +
              `${g.totalReturn ? " TR" : ""} ` +
              `(lines ${g.lines.join(", ")} — kept ${g.keptLine}, removed ${g.removedLines.join(", ")})`,
          )
          .join("; ") +
        `. A price return and a total return of one fund are NOT duplicates and are never merged.`,
      detail: {
        groups: identityCollapsed.map((g) => ({
          fundId: g.fundId,
          isin: g.isin,
          totalReturn: g.totalReturn,
          value: g.value,
          names: g.names,
          count: g.count,
          lines: g.lines,
          keptLine: g.keptLine,
          keptName: g.keptName,
          removedLines: g.removedLines,
        })),
        rowsRemoved: removedCount,
      },
    });
  }

  if (identityConflicts.length > 0) {
    refusals.push({
      code: "duplicate_holding_conflict",
      message:
        `${identityConflicts.length} fund${identityConflicts.length === 1 ? "" : "s"} appear${
          identityConflicts.length === 1 ? "s" : ""
        } under more than one name with DIFFERENT values: ` +
        identityConflicts
          .map(
            (c) =>
              `fund ${c.fundId}${c.isin ? ` (${c.isin})` : ""}${c.totalReturn ? " TR" : ""} — ` +
              c.variants
                .map(
                  (v) =>
                    `${v.value === null ? "no value" : v.value} from ${v.names
                      .map((n) => `"${n}"`)
                      .join(", ")} (line${v.lines.length === 1 ? "" : "s"} ${v.lines.join(", ")})`,
                )
                .join(" vs "),
          )
          .join("; ") +
        `. These resolve to ONE ba_cbam_funds row and the same measure, so they are one holding ` +
        `stated twice at odds with itself — spelling the fund two ways does not make it two funds. ` +
        `Nothing was collapsed and nothing was chosen. Resolve it in FE and re-export.`,
      detail: { conflicts: identityConflicts },
    });
  }

  if (conflicts.length > 0) {
    refusals.push({
      code: "duplicate_row_conflict",
      message:
        `${conflicts.length} name${conflicts.length === 1 ? " appears" : "s appear"} more than once with ` +
        `DIFFERENT values: ` +
        conflicts
          .map(
            (c) =>
              `"${c.name}" — ` +
              c.variants
                .map(
                  (v) =>
                    `${v.value === null ? "no value" : v.value} (line${
                      v.lines.length === 1 ? "" : "s"
                    } ${v.lines.join(", ")})`,
                )
                .join(" vs "),
          )
          .join("; ") +
        `. Nothing was collapsed and nothing was chosen: one of those values is wrong, the file ` +
        `does not say which, and keeping either would publish a figure that only looks unanimous. ` +
        `Resolve it in FE and re-export.`,
      detail: { conflicts },
    });
  }

  // ---------------------------------------------------------------- books
  const bookRows = rows.filter((r) => r.klass === "cbam_book");
  const bookByCode = new Map<string, ClassifiedRow[]>();
  for (const r of bookRows) {
    const code = r.book!.code;
    const list = bookByCode.get(code);
    if (list) list.push(r);
    else bookByCode.set(code, [r]);
  }
  const missingBooks = REQUIRED_BOOK_CODES.filter((c) => !bookByCode.has(c));
  if (missingBooks.length > 0) {
    refusals.push({
      code: "cbam_books_incomplete",
      message: `Only ${REQUIRED_BOOK_CODES.length - missingBooks.length} of the 11 CBAM books are present. Missing: ${missingBooks.join(", ")}. Re-run the FE pull with the full book list.`,
      detail: { missing: missingBooks, found: Array.from(bookByCode.keys()).sort() },
    });
  }

  // ---------------------------------------------------------------- benchmarks
  const benchRows = rows.filter((r) => r.klass === "cbam_benchmark");
  const benchByLevel = new Map<RiskLevel, ClassifiedRow>();
  for (const r of benchRows) {
    if (!benchByLevel.has(r.benchmark!.level)) benchByLevel.set(r.benchmark!.level, r);
  }
  const missingBench = RISK_LEVELS.filter((l) => !benchByLevel.has(l));
  if (missingBench.length > 0) {
    refusals.push({
      code: "cbam_benchmark_missing",
      message: `The CBAM benchmark ladder is incomplete — missing ${missingBench
        .map((l) => `CBAM ${ROMAN_BY_LEVEL[l]} Benchmark (level ${l})`)
        .join(", ")}. All five rungs are required.`,
      detail: { missingLevels: missingBench },
    });
  }

  // ---------------------------------------------------------------- peers
  const peerRows = rows.filter((r) => r.klass === "peer_constituent");
  const peerByCell = new Map<string, ClassifiedRow[]>();
  for (const r of peerRows) {
    const k = peerCellKey(r.peer!.house, r.peer!.level);
    const list = peerByCell.get(k);
    if (list) list.push(r);
    else peerByCell.set(k, [r]);
  }

  const missingCells = allPeerCells().filter((c) => !peerByCell.has(peerCellKey(c.house, c.level)));
  if (missingCells.length > 0) {
    // WARNING, not a refusal. The published CBAM I-V figures are their own rows in this same file,
    // so a missing constituent does not deprive us of the benchmark — only of the ability to verify
    // it by construction at the affected levels. The file remains usable; the affected levels are
    // simply taken as supplied rather than checked. See `composite_not_evaluated` for the per-level
    // consequence and `composite_reconcile_failed` for the case that IS a refusal.
    warnings.push({
      code: "peer_constituents_incomplete",
      message:
        `${peerByCell.size} of the ${PEER_CELL_COUNT} benchmark constituents (9 houses x 5 levels) are in this file. ` +
        `Missing: ${missingCells.map((c) => `${c.houseLabel} level ${c.level}`).join(", ")}. ` +
        `The benchmark values themselves are supplied by the file and are unaffected — but the affected levels ` +
        `cannot be checked against the equal-weight mean of their nine constituents, so they are accepted as published rather than verified.`,
      detail: {
        found: peerByCell.size,
        expected: PEER_CELL_COUNT,
        missing: missingCells.map((c) => ({ house: c.house, houseLabel: c.houseLabel, level: c.level })),
      },
    });
  }

  const dupCells = Array.from(peerByCell.entries()).filter(([, g]) => g.length > 1);
  if (dupCells.length > 0) {
    refusals.push({
      code: "peer_constituent_duplicate_cell",
      message: `Two or more rows claim the same benchmark constituent cell: ${dupCells
        .map(([k, g]) => `${k} (lines ${g.map((r) => r.line).join(", ")})`)
        .join("; ")}. The equal-weight mean would double-count.`,
      detail: {
        cells: dupCells.map(([k, g]) => ({
          cell: k,
          lines: g.map((r) => r.line),
          names: g.map((r) => r.rawName),
        })),
      },
    });
  }

  // -------------------------------------------- structural rows with no value
  const structural = rows.filter(
    (r) =>
      (r.klass === "cbam_book" || r.klass === "cbam_benchmark" || r.klass === "peer_constituent") &&
      r.value === null,
  );
  if (structural.length > 0) {
    refusals.push({
      code: "structural_row_missing_value",
      message: `${structural.length} row${structural.length === 1 ? "" : "s"} that must carry a value do not: ${structural
        .map((r) => `"${r.rawName}" (line ${r.line}, ${r.klass})`)
        .join("; ")}. A CBAM book, benchmark or benchmark constituent with no return cannot be ingested — and is not zero.`,
      detail: {
        rows: structural.map((r) => ({ line: r.line, name: r.rawName, klass: r.klass })),
      },
    });
  }

  // ---------------------------------------------------------------- soft checks
  const unclassified = rows.filter((r) => r.klass === "unclassified");
  if (unclassified.length > 0) {
    warnings.push({
      code: "unclassified_row",
      message: `${unclassified.length} row${unclassified.length === 1 ? "" : "s"} could not be classified: ${unclassified
        .map((r) => `"${r.rawName}" (line ${r.line}, ${r.unclassifiedReason})`)
        .join("; ")}. "Pounds Sterling" is FE's currency baseline, not an instrument — it must not be bound to the CASH pseudo-fund.`,
      detail: {
        rows: unclassified.map((r) => ({
          line: r.line,
          name: r.rawName,
          reason: r.unclassifiedReason,
        })),
      },
    });
  }

  if (counts.ia_sector !== expectedIa) {
    warnings.push({
      code: "ia_sector_count_unexpected",
      message: `Found ${counts.ia_sector} IA sector rows; ${expectedIa} were expected. The IA sector list does change — confirm the difference is a real IA reclassification before signing off.`,
      detail: { found: counts.ia_sector, expected: expectedIa },
    });
  }

  const holdingRows = rows.filter((r) => r.klass === "holding");
  const unmatched = holdingRows.filter((r) => r.holding && r.holding.fundId === null);
  if (index && unmatched.length > 0) {
    warnings.push({
      code: "holding_not_in_roster",
      message: `${unmatched.length} holding${unmatched.length === 1 ? "" : "s"} did not match any ba_cbam_funds row exactly: ${unmatched
        .map((r) => `"${r.rawName}" (line ${r.line})`)
        .join("; ")}. No fuzzy match was attempted — a near-match on manager and fund name would bind the wrong ISIN.`,
      detail: {
        rows: unmatched.map((r) => ({ line: r.line, name: r.rawName })),
      },
    });
  }
  const ambiguous = holdingRows.filter((r) => r.holding?.ambiguous);
  if (ambiguous.length > 0) {
    warnings.push({
      code: "holding_match_ambiguous",
      message: `${ambiguous.length} holding${ambiguous.length === 1 ? "" : "s"} matched more than one ba_cbam_funds row and is_current did not settle it: ${ambiguous
        .map((r) => `"${r.rawName}" (line ${r.line}) -> ${r.holding!.candidateFundIds.join(" / ")}`)
        .join("; ")}.`,
      detail: {
        rows: ambiguous.map((r) => ({
          line: r.line,
          name: r.rawName,
          candidates: r.holding!.candidateFundIds,
        })),
      },
    });
  }
  const holdingNoValue = holdingRows.filter((r) => r.value === null);
  if (holdingNoValue.length > 0) {
    warnings.push({
      code: "holding_missing_value",
      message: `${holdingNoValue.length} holding row${holdingNoValue.length === 1 ? " has" : "s have"} no value: ${holdingNoValue
        .map((r) => `"${r.rawName}" (line ${r.line})`)
        .join("; ")}.`,
      detail: { rows: holdingNoValue.map((r) => ({ line: r.line, name: r.rawName })) },
    });
  }

  const iaNoValue = rows.filter((r) => r.klass === "ia_sector" && r.value === null);
  if (iaNoValue.length > 0) {
    warnings.push({
      code: "ia_sector_missing_value",
      message: `${iaNoValue.length} IA sector row${iaNoValue.length === 1 ? " has" : "s have"} no value at all (the field is absent, not zero): ${iaNoValue
        .map((r) => `"${r.rawName}" (line ${r.line})`)
        .join("; ")}.`,
      detail: { rows: iaNoValue.map((r) => ({ line: r.line, name: r.rawName })) },
    });
  }

  const compNoValue = rows.filter((r) => r.klass === "external_comparator" && r.value === null);
  if (compNoValue.length > 0) {
    warnings.push({
      code: "comparator_missing_value",
      message: `${compNoValue.length} AFI/ARC comparator row${compNoValue.length === 1 ? " has" : "s have"} no value: ${compNoValue
        .map((r) => `"${r.rawName}" (line ${r.line})`)
        .join("; ")}.`,
      detail: { rows: compNoValue.map((r) => ({ line: r.line, name: r.rawName })) },
    });
  }

  // ---------------------------------------------------- F9, the construction check
  const composite = RISK_LEVELS.map<CompositeLevelCheck>((level) => {
    const cells = allPeerCells().filter((c) => c.level === level);
    const values: number[] = [];
    const missingHouses: PeerHouseId[] = [];
    for (const c of cells) {
      const g = peerByCell.get(peerCellKey(c.house, c.level));
      // A cell claimed twice is not usable either — the mean would double-count. That case is
      // already a refusal (peer_constituent_duplicate_cell); here it simply blocks evaluation.
      const v = g && g.length === 1 ? g[0].value : null;
      if (v !== null) values.push(v);
      else missingHouses.push(c.house);
    }
    const published = benchByLevel.get(level)?.value ?? null;
    const complete = values.length === cells.length;
    const mean = complete ? values.reduce((a, b) => a + b, 0) / values.length : null;
    const diff = mean !== null && published !== null ? mean - published : null;
    const verdict =
      diff === null ? "not_evaluated" : Math.abs(diff) <= tolerance ? "tie" : "gap";
    return {
      level,
      expected: cells.length,
      present: values.length,
      missingHouses,
      mean,
      published,
      diff,
      tolerance,
      verdict,
    };
  });

  for (const c of composite) {
    if (c.verdict === "gap") {
      // REFUSAL. All nine constituents were present, the check ran, and it FAILED. Either the
      // roster or the published benchmark is wrong and we cannot tell which — so neither may be
      // reported. This is the opposite case to a missing constituent (a warning): there we lose
      // the check, here the check convicts the data.
      refusals.push({
        code: "composite_reconcile_failed",
        message: `Level ${level_roman(c.level)}: all nine constituents are present, but their equal-weight mean is ${fmt(c.mean)} against a published CBAM ${level_roman(c.level)} Benchmark of ${fmt(c.published)} — a gap of ${fmt(c.diff)}pp, outside the ${c.tolerance}pp tolerance. Reconcile-by-construction has FAILED: either the constituent roster or the published benchmark is wrong. This file cannot be used at this level until that is resolved.`,
        detail: { level: c.level, mean: c.mean, published: c.published, diff: c.diff, tolerance: c.tolerance },
      });
    } else if (c.verdict === "not_evaluated") {
      warnings.push({
        code: "composite_not_evaluated",
        message: `Level ${level_roman(c.level)}: reconcile-by-construction could not be evaluated — ${c.present} of ${c.expected} constituents present${
          c.missingHouses.length ? ` (missing ${c.missingHouses.join(", ")})` : ""
        }${c.published === null ? "; the published benchmark is also absent" : ""}. No partial mean was computed: an eight-house mean is a different number, not an approximation.`,
        detail: {
          level: c.level,
          present: c.present,
          expected: c.expected,
          missingHouses: c.missingHouses,
          published: c.published,
        },
      });
    }
  }

  // ---------------------------------------------------------------- AFI blend
  const afiRows = rows.filter((r) => r.klass === "external_comparator" && r.comparator?.family === "afi");
  const balanced = afiRows.find((r) => r.comparator!.label === "Balanced")?.value ?? null;
  const aggressive = afiRows.find((r) => r.comparator!.label === "Aggressive")?.value ?? null;
  const supplied = afiRows.find((r) => r.comparator!.suppliedBlend)?.value ?? null;
  const computed = balanced !== null && aggressive !== null ? (balanced + aggressive) / 2 : null;
  const afiBlend: AfiBlendCheck = {
    level: 4,
    balanced,
    aggressive,
    computed,
    supplied,
    diff: computed !== null && supplied !== null ? Math.abs(computed - supplied) : null,
  };

  return {
    ok: refusals.length === 0,
    provenance: table.provenance,
    rows,
    counts,
    dedupe,
    composite,
    afiBlend,
    refusals,
    warnings,
  };
}

function emptyComposite(tolerance: number): CompositeLevelCheck[] {
  return RISK_LEVELS.map((level) => ({
    level,
    expected: 9,
    present: 0,
    missingHouses: [],
    mean: null,
    published: null,
    diff: null,
    tolerance,
    verdict: "not_evaluated" as const,
  }));
}

function level_roman(level: RiskLevel): string {
  return ROMAN_BY_LEVEL[level];
}

function fmt(n: number | null): string {
  return n === null ? "n/a" : n.toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
}
